// Le Répertoire — the app's front door. The left tree IS the repo's taxonomy;
// a row is a PROCESS (file and/or cycle record), never a database row.
// Layout per ux-design §2; classes per the kit spec (toolbar/chips/table).
import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, ExternalLink, RefreshCw, FolderOpen, Layers3 } from 'lucide-react';
import { api } from '../api/client';
import { GithubAccount } from '../components/GithubAccount';
import { useRole } from '../context/RoleContext';
import { useSearch } from '../context/SearchContext';
import { useT } from '../i18n';
import { formatDate } from '../utils/format';
import { NewProcessModal } from '../components/NewProcessModal';

import type { CatalogueRow } from '../types/catalogue';

type Filter = 'all' | 'live' | 'cycle' | 'documented' | 'tomodel' | 'tickets';

/** Row state, in the répertoire's five-value vocabulary. */
function rowState(r: CatalogueRow): 'live' | 'cycle' | 'documented' | 'tomodel' | 'draft' | 'superseded' {
  if (r.kind === 'draft') return 'draft';
  if (r.record) {
    if (r.record.state === 'superseded') return 'superseded';
    // "En service" means the process is actually live (phase 8) — NOT merely
    // accepted into the inventory at phase 5. Those are different facts.
    if (r.record.state === 'live' || r.record.phase_key === 'complete') return 'live';
    return 'cycle';
  }
  return r.isEmpty ? 'tomodel' : 'documented';
}

function StateChip({ r, t, lang }: { r: CatalogueRow; t: any; lang: string }) {
  const state = rowState(r);
  if (r.record?.state === 'cancelled') return <span className="state-chip">{t('rep.cancelled')}</span>;
  if (r.record?.state === 'on_hold') return <span className="state-chip state-chip--warn">{t('rep.paused')}</span>;
  if (r.record?.workflow_mode === 'revision' && r.record.state === 'active') return <span className="state-chip state-chip--cycle">{t(`control.${r.record.work_status || 'inbox'}`)}</span>;
  if (r.parseError) return <span className="state-chip state-chip--warn" title={r.parseError}>{t('rep.state.unreadable')}</span>;
  switch (state) {
    case 'live': return <span className="state-chip state-chip--live">{t('rep.state.live')}</span>;
    case 'superseded': return <span className="state-chip state-chip--superseded">{r.record?.superseded_by ? t('rep.state.superseded', { code: r.record.superseded_by }) : t('rep.state.supersededBare')}</span>;
    case 'cycle': return (
      <span className="state-chip state-chip--cycle">
        {t('rep.state.step', { n: r.record!.phase_order })} · {lang === 'fr' ? r.record!.phase_label_fr : r.record!.phase_label_en}
      </span>
    );
    case 'documented': return <span className="state-chip state-chip--documented" title={t('rep.documentedTip')}>{t('rep.state.documented')}</span>;
    case 'tomodel': return <span className="state-chip state-chip--tomodel" title={t('rep.toModelTip')}>{t('rep.state.toModel')}</span>;
    case 'draft': return <span className="state-chip state-chip--draft" title={t('rep.draftTip')}>{t('rep.state.draft')}</span>;
  }
}

interface TreeNode { key: string; label: string; depth: number; count: number; children: TreeNode[] }

function buildTree(rows: CatalogueRow[], rootLabel: string, t: any): TreeNode {
  const root: TreeNode = { key: '', label: rootLabel, depth: 0, count: rows.length, children: [] };
  const ensure = (parent: TreeNode, key: string, label: string, depth: number): TreeNode => {
    let node = parent.children.find(c => c.key === key);
    if (!node) { node = { key, label, depth, count: 0, children: [] }; parent.children.push(node); }
    return node;
  };
  for (const r of rows) {
    const tier = ensure(root, r.tier, r.tier, 1);
    tier.count++;
    let parent = tier;
    let key = r.tier;
    if (r.areaSegments.length === 0 && r.kind === 'file') {
      const rootNode = ensure(tier, `${key}//`, t('rep.root'), 2);
      rootNode.count++;
      continue;
    }
    r.areaSegments.forEach((seg, i) => {
      key = `${key}/${seg}`;
      parent = ensure(parent, key, seg, 2 + i);
      parent.count++;
    });
  }
  const sortRec = (n: TreeNode) => { n.children.sort((a, b) => a.label.localeCompare(b.label)); n.children.forEach(sortRec); };
  sortRec(root);
  return root;
}

export function RepertoirePage() {
  const { t, lang } = useT();
  const { isCde } = useRole();
  const { searchTerm } = useSearch();
  const [data, setData] = useState<{ repo: any; rows: CatalogueRow[] } | null>(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [selectedNode, setSelectedNode] = useState('');
  const [groupVersions, setGroupVersions] = useState(true);
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());
  const [showTechnical, setShowTechnical] = useState(false);
  const [creating, setCreating] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [indexOpen, setIndexOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const refresh = async () => {
    setRefreshing(true); setRefreshError(false);
    try { await api.refreshRepoIndex(); await load(); } catch { setRefreshError(true); }
    finally { setRefreshing(false); }
  };

  const load = () => api.getCatalogue().then(d => { setData(d); setError(false); });
  useEffect(() => {
    load().then(() => {}).catch(() => setError(true));
  }, []);
  useEffect(() => {
    if(data) setCollapsed(new Set(data.rows.filter(r=>r.areaSegments.length>0).map(r=>`${r.tier}/${r.areaSegments[0]}`)));
  }, [data?.repo.builtAt]);

  useEffect(() => {
    api.getRepoStatus().then(() => {}).catch(() => {});
    fetch('/api/admin/settings', { credentials: 'include' }).then(r => r.ok ? r.json() : null)
      .then(s => { if (s?.show_technical_files === 'true') setShowTechnical(true); }).catch(() => {});
  }, []);

  const rows = data?.rows ?? [];
  const visible = useMemo(() => {
    let out = rows.filter(r => showTechnical || !r.isTechnical);
    if (selectedNode) {
      out = out.filter(r => {
        const path = [r.tier, ...r.areaSegments].join('/');
        if (selectedNode.endsWith('//')) return r.tier === selectedNode.slice(0, -2) && r.areaSegments.length === 0;
        return path === selectedNode || path.startsWith(selectedNode + '/');
      });
    }
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      out = out.filter(r =>
        r.title.toLowerCase().includes(q) ||
        (r.path ?? '').toLowerCase().includes(q) ||
        (r.record?.process_code ?? '').toLowerCase().includes(q) ||
        (r.record?.consultant_name ?? '').toLowerCase().includes(q) ||
        (r.record?.functions ?? []).some(f => f.toLowerCase().includes(q)) ||
        (r.lanes ?? []).some(l => l.toLowerCase().includes(q))
      );
    }
    if (filter !== 'all') {
      out = out.filter(r => {
        const s = rowState(r);
        if (filter === 'live') return s === 'live';
        if (filter === 'cycle') return ['active','on_hold'].includes(r.record?.state ?? '');
        if (filter === 'documented') return s === 'documented';
        if (filter === 'tomodel') return s === 'tomodel';
        if (filter === 'tickets') return r.openTickets > 0;
        return true;
      });
    }
    return out;
  }, [rows, selectedNode, searchTerm, filter, showTechnical]);

  // version families: one row per family, most-advanced variant leads
  const grouped = useMemo(() => {
    if (!groupVersions) return visible.map(r => ({ lead: r, variants: [] as CatalogueRow[] }));
    const families = new Map<string, CatalogueRow[]>();
    for (const r of visible) families.set(r.familyKey, [...(families.get(r.familyKey) ?? []), r]);
    const rank = (r: CatalogueRow) => (r.record ? 100 + r.record.phase_order : 0)
      + (r.variant?.startsWith('TO-BE') ? 50 : 0)
      + (Number((r.variant?.match(/V(\d+)/) ?? [])[1]) || 0);
    return [...families.values()].map(members => {
      const sorted = [...members].sort((a, b) => rank(b) - rank(a));
      return { lead: sorted[0], variants: sorted.slice(1) };
    });
  }, [visible, groupVersions]);

  // group rows by tier, then area, preserving repo order
  const sections = useMemo(() => {
    const byTier = new Map<string, Map<string, typeof grouped>>();
    for (const g of grouped) {
      const area = g.lead.areaSegments.join(' / ') || t('rep.root');
      if (!byTier.has(g.lead.tier)) byTier.set(g.lead.tier, new Map());
      const tierMap = byTier.get(g.lead.tier)!;
      tierMap.set(area, [...(tierMap.get(area) ?? []), g]);
    }
    return [...byTier.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [grouped, t]);

  const tree = useMemo(
    () => buildTree(rows.filter(r => showTechnical || !r.isTechnical), t('rep.allProcesses'), t),
    [rows, showTechnical, t]
  );

  const open = (r: CatalogueRow) => {
    if (r.record) window.location.hash = `#/process/${r.record.id}`;
    else if (r.path) window.location.hash = `#/process/file?path=${encodeURIComponent(r.path)}`;
  };

  const syncedAgo = () => {
    if (!data?.repo?.builtAt) return '';
    const mins = Math.floor((Date.now() - new Date(data.repo.builtAt).getTime()) / 60000);
    return mins < 1 ? t('rep.justNow') : t('rep.minutesAgo', { n: mins });
  };

  if (error) return <div className="empty-state"><div className="empty-state-title">{t('rep.repoDown')}</div></div>;
  if (!data) return <div className="page-loading"><div className="spinner" /></div>;

  const renderNode = (node: TreeNode): React.ReactNode => {
    const isOpen = !collapsed.has(node.key);
    return <li key={node.key}>
      <div className={`rep-index-row rep-index-depth-${node.depth}${selectedNode === node.key ? ' active' : ''}`}>
        {node.children.length > 0 ? <button className="btn-icon rep-disclosure" aria-expanded={isOpen}
          aria-label={t(isOpen ? 'rep.collapse' : 'rep.expand', {name:node.label})}
          onClick={() => setCollapsed(s => {const n=new Set(s);n.has(node.key)?n.delete(node.key):n.add(node.key);return n;})}>
          {isOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
        </button> : <span className="rep-disclosure"/>}
        <button className="rep-index-select" aria-current={selectedNode === node.key ? 'location' : undefined}
          onClick={() => {setSelectedNode(node.key);setIndexOpen(false);}}>
          <span>{node.label.replace(/^\d+\s+/, '')}</span><span className="rep-tree-count">{node.count}</span>
        </button>
      </div>
      {isOpen && node.children.length>0 && <ul>{node.children.map(renderNode)}</ul>}
    </li>;
  };

  const filterChips: { key: Filter; label: string }[] = [
    { key: 'all', label: t('rep.filter.all') },
    { key: 'live', label: t('rep.filter.live') },
    { key: 'cycle', label: t('rep.filter.cycle') },
    { key: 'documented', label: t('rep.filter.documented') },
    { key: 'tomodel', label: t('rep.filter.toModel') },
    { key: 'tickets', label: t('rep.filter.tickets') },
  ];

  return (
    <div className="repertoire-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('shell.title.repository')}</h1>
          <p className="page-meta">{t(groupVersions?'rep.familyCount':'rep.entryCount', { n: grouped.length })}</p>
        </div>
        <div className="flex items-center gap-16" style={{ flexWrap: 'wrap' }}>
          <GithubAccount variant="inline" />
          {isCde && <button className="btn" disabled={refreshing} onClick={refresh}>
            <RefreshCw className="icon-sm"/> {t(refreshing ? 'rep.refreshing' : 'rep.refresh')}
          </button>}
          {isCde && (
            <button className="btn btn-accent" onClick={() => setCreating(true)}>
              <Plus className="icon-sm" /> {t('rep.new')}
            </button>
          )}
        </div>
      </div>

      {!data.repo.configured && (
        <div className="empty-state-card mb-16"><div className="empty-state">
          <div className="empty-state-desc">{t('rep.repoDown')}</div>
        </div></div>
      )}

      <div className="toolbar">
        <div className="filter-chips">
          {filterChips.map(f => (
            <button key={f.key} className={`chip ${filter === f.key ? 'active' : ''}`}
                    onClick={() => setFilter(filter === f.key ? 'all' : f.key)}>
              {f.label}
            </button>
          ))}
        </div>
        <label className="toggle toggle-compact">
          <input type="checkbox" className="toggle-input" checked={groupVersions} onChange={e => setGroupVersions(e.target.checked)} />
          <span className="toggle-slider" />
          <span className="toggle-label">{t('rep.groupVersions')}</span>
        </label>
      </div>

      {refreshError && <p role="alert" className="text-danger">{t('rep.refreshError')}</p>}
      <div className="rep-layout">
        <aside className="rep-tree">
          <button className="rep-mobile-index btn" aria-expanded={indexOpen} onClick={()=>setIndexOpen(!indexOpen)}>
            <FolderOpen size={16}/> {t('rep.index')} <ChevronDown size={16}/>
          </button>
          <nav className={`rep-index${indexOpen ? ' open' : ''}`} aria-label={t('rep.index')}>
            <div className="rep-index-heading">{t('rep.index')}</div>
            <button className={`rep-index-root${!selectedNode?' active':''}`} aria-current={!selectedNode?'location':undefined}
              onClick={()=>{setSelectedNode('');setIndexOpen(false);}}>
              <Layers3 size={18}/><span>{t('rep.allProcesses')}</span><span className="rep-tree-count">{tree.count}</span>
            </button>
            <ul className="rep-index-tiers">{tree.children.map(renderNode)}</ul>
          </nav>
          <div className="rep-foot">
            {data.repo.configured && <>
              <span>{t('rep.branch', {branch:'main'})}</span>
              <span>{t('rep.syncedAgo', {when:syncedAgo()})}</span>
              <a className="text-accent" href="https://github.com/Mr-Moonsilver/eveil-global-processes" target="_blank" rel="noreferrer">
                {t('rep.openGithub')} <ExternalLink size={11}/>
              </a>
            </>}
          </div>
        </aside>
        <div className="rep-results">
          <div className="rep-context" aria-label={t('rep.location')}>
            <button onClick={()=>setSelectedNode('')}>{t('rep.allProcesses')}</button>
            {selectedNode.split('/').filter(Boolean).map((s,i)=><span key={i}><ChevronRight size={12}/>{s.replace(/^\d+\s+/,'')}</span>)}
            <span className="rep-context-count">{t('rep.entryCount',{n:visible.length})}</span>
          </div>
          <p className="rep-legend">{t('rep.followupHelp')}</p>
          {visible.length===0 ? <div className="empty-state">
            <div className="empty-state-title">{searchTerm?t('rep.noResults',{q:searchTerm}):t('rep.emptyDomain')}</div>
            <button className="btn" onClick={()=>{setSelectedNode('');setFilter('all');}}>{t('rep.resetFilters')}</button>
          </div> : <div className="rep-table-scroll" tabIndex={0} role="region" aria-label={t('rep.allProcesses')}>
            <table className="table rep-table">
              <thead><tr>
                <th aria-label={t('rep.variants')}/>
                <th>{t('rep.col.process')}</th><th>{t('rep.col.state')}</th><th>{t('rep.col.owner')}</th>
                <th>{t('rep.col.functions')}</th><th title={t('rep.actorsHelp')}>{t('rep.col.roles')}</th>
                <th>{t('rep.col.modified')}</th><th aria-label={t('rep.filter.tickets')}/>
              </tr></thead>
              <tbody>
                {sections.flatMap(([tier,areas])=>[
                  <tr key={tier} className="rep-tier-row"><th scope="rowgroup" colSpan={8}>{tier.replace(/^\d+\s+/,'')}</th></tr>,
                  ...[...areas.entries()].flatMap(([area,groups])=>[
                    <tr key={tier+area} className="rep-domain-row"><th scope="rowgroup" colSpan={8}>{area}</th></tr>,
                    ...groups.map(g=><FragmentRows key={g.lead.path??g.lead.record?.id} group={g}
                      expanded={expandedFamilies.has(g.lead.familyKey)} toggle={()=>setExpandedFamilies(s=>{const n=new Set(s);n.has(g.lead.familyKey)?n.delete(g.lead.familyKey):n.add(g.lead.familyKey);return n;})}
                      open={open} t={t} lang={lang}/>),
                  ]),
                ])}
              </tbody>
            </table>
          </div>}
        </div>
      </div>

      {creating && (
        <NewProcessModal
          defaultNode={selectedNode}
          onClose={() => setCreating(false)}
          onCreated={(id) => { setCreating(false); window.location.hash = `#/process/${id}`; }}
        />
      )}
    </div>
  );
}

function FragmentRows({group,expanded,toggle,t,lang}: {
  group:{lead:CatalogueRow;variants:CatalogueRow[]};expanded:boolean;toggle:()=>void;
  open:(r:CatalogueRow)=>void;t:any;lang:string;
}) {
  const render=(r:CatalogueRow,variant=false)=>{
    const link=r.record?`#/process/${r.record.id}`:`#/process/file?path=${encodeURIComponent(r.path!)}`;
    const commit=r.lastCommit;
    return <tr key={r.path??r.record?.id} className={variant?'rep-variant-row':''}>
      <td>{!variant && group.variants.length>0 && <button className="btn-icon" aria-expanded={expanded} aria-label={t('rep.variantsFor',{name:r.title})} onClick={toggle}>
        {expanded?<ChevronDown size={15}/>:<ChevronRight size={15}/>}</button>}</td>
      <td className="rep-title-cell"><div className="rep-title-line">
        <a className="rep-title" href={link} title={r.title}>{r.title}</a>
        {r.variant && <span className="variant-chip on">{r.variant}</span>}
        {!variant && group.variants.length>0 && <button className="rep-version-count" onClick={toggle} aria-expanded={expanded}>{t('rep.variantCount',{n:group.variants.length})}</button>}
        {r.isDuplicate && <span className="state-chip state-chip--warn" title={t('rep.chip.duplicateTip',{paths:r.duplicateOf.join(' · ')})}>{t('rep.chip.duplicate')}</span>}
        {r.parseError && <span className="state-chip state-chip--warn" title={r.parseError}>{t('rep.state.unreadable')}</span>}
        {r.importReview && <span className="state-chip state-chip--warn" title={r.importReview}>{t('rep.importReviewShort')}</span>}
      </div></td>
      <td><StateChip r={r} t={t} lang={lang}/></td>
      <td className="rep-assignee" title={r.record?.consultant_name??undefined}>{r.record?.consultant_name || <span className="text-ink-3">{t('rep.unassigned')}</span>}</td>
      <td title={r.record?.functions.join(", ")}>{r.record?.functions.length ? r.record.functions.map(f=><span key={f} className="badge">{f}</span>):<span className="text-ink-3">{t('rep.unrecorded')}</span>}</td>
      <td>{r.lanes?.length ? <details className="rep-actors"><summary>{r.lanes.length}</summary><div><p>{t('rep.actorsHelp')}</p>{r.lanes.map(l=><span className="badge" key={l}>{l}</span>)}</div></details>:<span className="text-ink-3">—</span>}</td>
      <td>{commit ? <a className="rep-commit" href={`https://github.com/Mr-Moonsilver/eveil-global-processes/commit/${commit.sha}`} target="_blank" rel="noreferrer" title={`${commit.author} · ${commit.message??""}`}>
        <time dateTime={commit.date}>{formatDate(commit.date,lang)}</time><span>{commit.author}</span>
      </a>:<span className="text-ink-3">{t(r.path?'rep.historyUnavailable':'rep.noFile')}</span>}</td>
      <td>{r.openTickets>0 && <a href={link} className="badge badge-warn" title={t('rep.filter.tickets')}>{r.openTickets}</a>}</td>
    </tr>;
  };
  return <>{render(group.lead)}{expanded && group.variants.map(r=>render(r,true))}</>;
}
